import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as model from "../../../models/MeetingsModel";
import { apiError } from "../../../helpers/ErrorHelper";
import { useAuth } from "../../../context/AuthContext";
import {
  DatePicker,
  MySelect,
  Text,
} from "../../inputs/inputs";
import { checkPermission } from "../../../helpers/GeneralHelper";
import dateFormat from "dateformat";

const emptyAction = {
  meeting_action_item: "",
  meeting_action_owner: "",
  meeting_action_due_date: null,
  meeting_action_priority: "Medium",
  meeting_action_status: "Open",
};

const priorityOptions = [
  { value: "High", label: "High" },
  { value: "Medium", label: "Medium" },
  { value: "Low", label: "Low" },
];

const statusOptions = [
  { value: "Open", label: "Open" },
  { value: "In Progress", label: "In Progress" },
  { value: "Done", label: "Done" },
];

const parseDueDate = (value) => {
  if (!value || value === "0000-00-00") return null;
  if (value instanceof Date && !isNaN(value.getTime())) return value;

  // Prefer YYYY-MM-DD from MySQL without timezone shift
  if (typeof value === "string") {
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const date = new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3])
      );
      return isNaN(date.getTime()) ? null : date;
    }
  }

  const parsed = new Date(Date.parse(value));
  return isNaN(parsed.getTime()) ? null : parsed;
};

const formatDueDateForApi = (value) => {
  const date = parseDueDate(value);
  return date ? dateFormat(date, "yyyy-mm-dd") : "";
};

export default function MeetingActions({
  meetingId,
  isView = false,
  moduleRoute = "meetings",
}) {
  const { state } = useAuth();
  const user_data = state.user_data;
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState(emptyAction);
  const [errors, setErrors] = useState({});
  const [editId, setEditId] = useState("");

  const canEdit = !isView && checkPermission(moduleRoute, "edit");

  const loadActions = async () => {
    if (!meetingId) return;
    setLoading(true);
    try {
      const list = await model.getActions(user_data.user_token, meetingId);
      setActions(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadActions();
  }, [meetingId]);

  const resetForm = () => {
    setFields(emptyAction);
    setErrors({});
    setEditId("");
  };

  const startEdit = (row) => {
    setEditId(row._meeting_action_id);
    setFields({
      meeting_action_item: row.meeting_action_item || "",
      meeting_action_owner: row.meeting_action_owner || "",
      meeting_action_due_date: parseDueDate(row.meeting_action_due_date),
      meeting_action_priority: row.meeting_action_priority || "Medium",
      meeting_action_status: row.meeting_action_status || "Open",
    });
    setErrors({});
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!fields.meeting_action_item?.trim()) {
      setErrors({ meeting_action_item: "Action item is required" });
      toast.error("Please enter an action item");
      return;
    }

    setSaving(true);
    try {
      const formData = {
        meeting_id: meetingId,
        meeting_action_item: fields.meeting_action_item,
        meeting_action_owner: fields.meeting_action_owner,
        meeting_action_due_date: formatDueDateForApi(fields.meeting_action_due_date),
        meeting_action_priority: fields.meeting_action_priority,
        meeting_action_status: fields.meeting_action_status,
      };

      let response;
      if (editId) {
        response = await model.updateAction(
          user_data.user_token,
          editId,
          formData
        );
      } else {
        response = await model.createAction(user_data.user_token, formData);
      }

      if (response.status) {
        toast.success(response.message);
        resetForm();
        loadActions();
      } else {
        setErrors(response.error || {});
        toast.error(response.message);
      }
    } catch (error) {
      apiError(error);
    }
    setSaving(false);
  };

  const removeRow = async (row) => {
    if (!window.confirm("Delete this action item?")) return;
    try {
      const response = await model.removeAction(
        user_data.user_token,
        row._meeting_action_id
      );
      if (response.status) {
        toast.success(response.message);
        loadActions();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      apiError(error);
    }
  };

  return (
    <div>
      {canEdit && (
        <form onSubmit={submit} className="mb-4">
          <div className="row gy-2">
            <div className="col-xl-6 mb-2">
              <Text
                column="meeting_action_item"
                label="Action Item"
                required={true}
                fields={fields}
                setFields={setFields}
                errors={errors}
              />
            </div>
            <div className="col-xl-6 mb-2">
              <Text
                column="meeting_action_owner"
                label="Owner"
                fields={fields}
                setFields={setFields}
                errors={errors}
              />
            </div>
            <div className="col-xl-4 mb-2">
              <DatePicker
                column="meeting_action_due_date"
                label="Due Date"
                fields={fields}
                setFields={setFields}
                errors={errors}
              />
            </div>
            <div className="col-xl-4 mb-2">
              <MySelect
                column="meeting_action_priority"
                label="Priority"
                options={priorityOptions}
                firstOption={false}
                fields={fields}
                setFields={setFields}
                errors={errors}
              />
            </div>
            <div className="col-xl-4 mb-2">
              <MySelect
                column="meeting_action_status"
                label="Status"
                options={statusOptions}
                firstOption={false}
                fields={fields}
                setFields={setFields}
                errors={errors}
              />
            </div>
            <div className="col-12">
              <button className="btn btn-primary btn-sm me-2" disabled={saving}>
                {saving
                  ? "Saving..."
                  : editId
                    ? "Update Action"
                    : "Add Action"}
              </button>
              {editId && (
                <button
                  type="button"
                  className="btn btn-light btn-sm"
                  onClick={resetForm}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-muted">Loading actions...</p>
      ) : actions.length === 0 ? (
        <p className="text-muted mb-0">No action items yet.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered table-sm align-middle">
            <thead>
              <tr>
                <th>#</th>
                <th>Action Item</th>
                <th>Owner</th>
                <th>Due Date</th>
                <th>Priority</th>
                <th>Status</th>
                {canEdit && <th>Action</th>}
              </tr>
            </thead>
            <tbody>
              {actions.map((row, index) => {
                const dueDate = parseDueDate(row.meeting_action_due_date);
                return (
                <tr key={row._meeting_action_id || index}>
                  <td>{index + 1}</td>
                  <td>{row.meeting_action_item}</td>
                  <td>{row.meeting_action_owner || "-"}</td>
                  <td>
                    {dueDate ? dateFormat(dueDate, "dd/mm/yyyy") : "-"}
                  </td>
                  <td>{row.meeting_action_priority || "-"}</td>
                  <td>{row.meeting_action_status || "-"}</td>
                  {canEdit && (
                    <td>
                      <div className="hstack gap-2">
                        <button
                          type="button"
                          className="btn btn-icon btn-sm btn-info"
                          title="Edit"
                          onClick={() => startEdit(row)}
                        >
                          <i className="bx bxs-edit"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-icon btn-sm btn-danger"
                          title="Delete"
                          onClick={() => removeRow(row)}
                        >
                          <i className="bx bxs-trash"></i>
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
